import React from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

interface AdminInfo {
  email: string;
  password: string;
  confirmPassword: string;
  displayName: string;
  agreeToTerms: boolean;
}

interface AdminInfoFormProps {
  onSubmit: (data: Omit<AdminInfo, 'confirmPassword' | 'agreeToTerms'>) => void;
  onBack: () => void;
}

const schema = yup.object().shape({
  email: yup
    .string()
    .email('正しいメールアドレスを入力してください')
    .required('メールアドレスは必須です'),
  password: yup
    .string()
    .min(8, 'パスワードは8文字以上で入力してください')
    .required('パスワードは必須です'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'パスワードが一致しません')
    .required('パスワードの確認は必須です'),
  displayName: yup
    .string()
    .min(1, '表示名は1文字以上で入力してください')
    .required('表示名は必須です'),
  agreeToTerms: yup
    .boolean()
    .oneOf([true], '利用規約に同意する必要があります')
    .required('利用規約に同意する必要があります'),
});

export const AdminInfoForm: React.FC<AdminInfoFormProps> = ({ onSubmit, onBack }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminInfo>({
    resolver: yupResolver(schema),
  });

  const onFormSubmit = (data: AdminInfo) => {
    const { confirmPassword, agreeToTerms, ...submitData } = data;
    onSubmit(submitData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)}>
      <Typography variant="h6" gutterBottom>
        管理者情報の入力
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        マンション管理組合の管理者となるアカウント情報を入力してください。
      </Typography>

      <Stack spacing={2}>
        <TextField
          {...register('email')}
          fullWidth
          label="メールアドレス"
          type="email"
          error={!!errors.email}
          helperText={errors.email?.message}
          placeholder="admin@example.com"
        />

        <TextField
          {...register('displayName')}
          fullWidth
          label="表示名"
          error={!!errors.displayName}
          helperText={errors.displayName?.message}
          placeholder="田中 太郎"
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            {...register('password')}
            fullWidth
            label="パスワード"
            type="password"
            error={!!errors.password}
            helperText={errors.password?.message}
          />

          <TextField
            {...register('confirmPassword')}
            fullWidth
            label="パスワード確認"
            type="password"
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              {...register('agreeToTerms')}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              <Link 
                href="/terms-of-service" 
                target="_blank" 
                rel="noopener noreferrer"
                color="primary"
                underline="hover"
              >
                利用規約
              </Link>
              に同意する
            </Typography>
          }
          sx={{ mt: 2 }}
        />
        {errors.agreeToTerms && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            {errors.agreeToTerms.message}
          </Typography>
        )}
      </Stack>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={onBack}>
          戻る
        </Button>
        <Button type="submit" variant="contained">
          次へ
        </Button>
      </Box>
    </Box>
  );
};